import { InstallAppBaseComponents } from './components/install';
import Phaser from 'phaser';
import { GAME_EVENTS, SETTINGS_EVENTS, VIEWPORT_EVENTS } from './constants/Events';
import { getDeviceMetric } from './utils/deviceDetection';
import { SpinePreviewer } from './scenes/SpinePreviewer';

/**
 * Start Here :)<br>
 *
 * AppBase is a base game class that extends [Phaser.Game]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Game.html}<br>
 * You should extend this class and pass a [getConfig()]{@link getConfigBase} configuration object through on initialization.<br>
 * The app base will take responsibilty for parsing the config file, initlializing phaser and installing any of the requested components.
 *
 * @param {Object} config Configuration object generated as a result of calling [getConfig()]{@link getConfigBase}
 */
export class AppBase extends Phaser.Game {
  constructor({ config, paths }) {
    // safari 10 fix
    var ua = window.navigator.userAgent;
    var msie = ua.indexOf('MSIE');
    if (!(msie > 0 || !!ua.match(/Trident.*rv\:11\./))) EventTarget = EventTarget || Element;

    const params = new URLSearchParams(window.location.search);
    if (params.has('canvas')) {
      config.type = Phaser.CANVAS;
    } else if (params.has('webgl')) {
      config.type = Phaser.WEBGL;
    }

    super(config);
    this._gameConfig = config;
    this._deviceMetric = getDeviceMetric();
    this._isLowEndDevice = this._deviceMetric.category == 'LOW_END';
    this._defaultDimensions = { width: config.width, height: config.height };
    this._safeDimensions = { width: config.safeWidth, height: config.safeHeight };
    this._forceRotation = config.forceRotation;
    if (paths?.base) this.config.loaderBaseURL = paths.base;

    InstallAppBaseComponents(this, config.components);
    this.events.once(Phaser.Core.Events.READY, () => this._handleGameReady({ paths }), this);
  }

  init() {}

  /**
   * Methods to call after the master load has been completed
   * Checks for default files and attempts to add them
   * You should call super.assetsLoaded() if extending this to ensure defaults are checked, or you can manually set them yourself.
   */
  assetsLoaded() {
    const copyxml = this.cache.xml.get('copy_xml');
    if (copyxml) this.copy.addCopyFromXML(copyxml);
  }

  /**
   * The previewer data object
   * @type {Object}
   * @readonly
   */
  get previewerData() {
    return null;
  }

  /**
   * The configuration object as passed through on initialization
   * @type {Object}
   * @readonly
   */
  get gameConfig() {
    return this._gameConfig;
  }

  /**
   * The estimated performance level of the device the game is running on
   * @type {string}
   * @readonly
   */
  get deviceMetric() {
    return this._deviceMetric;
  }

  /**
   * The estimated performance level of the device the game is running on
   * @type {string}
   * @readonly
   */
  get isLowEndDevice() {
    return this._isLowEndDevice;
  }

  /**
   * The default game dimension as set in the configuration object passed through on initialization
   * @type {object}
   * @readonly
   */
  get defaultDimensions() {
    return this._defaultDimensions;
  }

  /**
   * The default game width as set in the configuration object passed through on initialization
   * @type {Number}
   * @readonly
   */
  get defaultWidth() {
    return this.defaultDimensions.width;
  }

  /**
   * The default game height as set in the configuration object passed through on initialization
   * @type {Number}
   * @readonly
   */
  get defaultHeight() {
    return this.defaultDimensions.height;
  }

  /**
   * The the center point of the game based on defaultDimensions
   * @type {object}
   * @readonly
   */
  get centerPoint() {
    return { x: this._defaultDimensions.width / 2, y: this._defaultDimensions.height / 2 };
  }

  /**
   * The screen safe dimensions as set in the configuration object passed through on initialization
   * @type {object}
   * @readonly
   */
  get safeDimensions() {
    return this._safeDimensions;
  }

  /**
   * The safe game width as set in the configuration object passed through on initialization
   * @type {Number}
   * @readonly
   */
  get safeWidth() {
    return this.safeDimensions.width;
  }

  /**
   * The safe game height as set in the configuration object passed through on initialization
   * @type {Number}
   * @readonly
   */
  get safeHeight() {
    return this.safeDimensions.height;
  }

  /**
   * Pause the Game.
   * Pauses the scene and sound controller, and dispatches a PAUSED event
   */
  pauseGame() {
    if (this.paused) {
      return;
    }
    this.paused = true;
    this.sceneController.pause();
    this.soundController.pause();
    this.events.emit(GAME_EVENTS.PAUSED);
  }

  /**
   * Resume the Game.
   * Resumes the scene and sound controller, and dispatches a RESUMED event
   */
  resumeGame() {
    // can't override rotation paused state
    if (!this.paused || this.rotatePaused) {
      return;
    }
    this.paused = false;
    this.sceneController.resume();
    this.soundController.resume();
    this.events.emit(GAME_EVENTS.RESUMED);
  }

  /**
   * Toggle game audio
   */
  toggleAudio() {
    this.settings.toggleAudio();
  }

  /**
   * Called after game ready event for actioning items that require game systems to be installed
   * @private
   */
  _handleGameReady(opts) {
    this.viewportController.updateViewport();
    this.viewportController.events.on(VIEWPORT_EVENTS.UPDATED, this._handleViewportUpdated, this);
    this.settings.events.on(SETTINGS_EVENTS.CHANGED, this.applyChangedSettings, this);
    this.appUrls.setPaths(opts?.paths);
    if (__SHORTCUTS_ENABLED__) this.shortcuts.enable();
    this.bootGame();
  }

  /**
   * Automatically called when phaser instance is booted and ready to function<br/>does nothing by default, should be overwritten
   */
  bootGame() {}

  showSpinePreviewer() {
    if (this.sceneController.currentSceneKey === 'SpinePreviewer') {
      return;
    }
    this.sceneController.addScene('SpinePreviewer', SpinePreviewer);
    this.sceneController.switchScene('SpinePreviewer', { data: {} });
    this.hud.setState('EMPTY');
  }

  /** @private */
  _handleViewportUpdated(viewport) {
    const rotationOK =
      this.forceRotation === 'landscape'
        ? viewport.width > viewport.height
        : this.forceRotation === 'portrait'
        ? viewport.width < viewport.height
        : true;
    if (!rotationOK) {
      console.log('Pausing due to unsupported rotation');
      this.rotatePaused = true;
      this.pauseGame();
    } else if (this.rotatePaused) {
      console.log('Resuming due to rotation');
      this.rotatePaused = false;
      this.resumeGame();
    }
  }

  // =============================================================================================
  // SETTINGS
  // =============================================================================================

  /**
   * @protected
   * @description Callback on settings changed, by default will update sound controller and captions.  <br>
   * Exposed so that you can override if you need to
   * @param {object} e
   */
  applyChangedSettings(e) {
    console.log('setting change >> ' + e.setting + ' : ' + e.value);
    this.captions.enabled = this.settings.captions;
    this.soundController.mute = !this.settings.audio;
  }
}
